"use client";

import { SectionIntro } from "../SectionIntro/SectionIntro";
import classNames from "classnames/bind";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import Image from "next/image";
import { ReactNode, useCallback, useEffect, useId, useState } from "react";
import styles from "./Locations.module.css";

const cx = classNames.bind(styles);

interface LocationCard {
  label: string;
  imageSrc: string;
  imageAlt: string;
  hoverImageSrc?: string;
  hoverImageAlt?: string;
}

interface LocationsProps {
  kicker?: string;
  headline: string;
  copy?: ReactNode;
  cards: LocationCard[];
}

export const Locations = ({
  kicker,
  headline,
  copy,
  cards,
}: LocationsProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      loop: false,
      skipSnaps: false,
      slidesToScroll: 1,
      containScroll: "trimSnaps",
      watchDrag: (api) => api.canScrollPrev() || api.canScrollNext(),
      dragFree: true,
    },
    [WheelGesturesPlugin({ forceWheelAxis: "x" })],
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScroll(emblaApi.canScrollPrev() || emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("reInit", onSelect);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const statusId = useId();

  if (!cards?.length) {
    return null;
  }

  return (
    <section className={cx("section", "locations")}>
      <div className={cx("container")}>
        <div className={cx("inner")}>
          <div className={cx("intro")}>
            <SectionIntro variant="split" kicker={kicker} headline={headline}>
              {copy}
            </SectionIntro>
          </div>
          <div id={statusId} className="visually-hidden" aria-live="polite">
            {`Slide ${selectedIndex + 1} of ${cards.length}`}
          </div>
          <div className={cx("embla-shell", { "is-scrollable": canScroll })}>
            <div className={cx("embla")}>
              <div className={cx("embla-viewport")} ref={emblaRef}>
                <div className={cx("embla-container")}>
                  {cards.map((card, index) => (
                    <div
                      className={cx("embla-slide")}
                      key={`location-card-${index}`}
                    >
                      <article className={cx("card")}>
                        {card.imageSrc ? (
                          <Image
                            src={card.imageSrc}
                            alt={card.imageAlt || card.label}
                            fill
                            className={cx("card-image", "card-image-primary")}
                            sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 80vw"
                          />
                        ) : null}
                        {card.hoverImageSrc ? (
                          <Image
                            src={card.hoverImageSrc}
                            alt={
                              card.hoverImageAlt || card.imageAlt || card.label
                            }
                            fill
                            className={cx("card-image", "card-image-hover")}
                            sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 80vw"
                          />
                        ) : null}
                        <div className={cx("card-overlay")} />
                        <h3 className={cx("card-title")}>{card.label}</h3>
                      </article>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
